import { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import Notification from "../Notification";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useStudioPlan } from "../../hooks/useStudioPlan";
import { CheckIcon } from "@heroicons/react/20/solid";
import BillingSetupWizard from "./BillingSetupWizard";
import {
  PENDING_PLATFORM_UPGRADE_KEY,
  redirectToBillingSetupWizard,
} from "../../hooks/useBillingSetupRedirect";

const PAID_UPGRADE_PLANS = [
  {
    id: "price_1Rt6VQKQZoTFogHvqWkAKnGl",
    name: "Launch",
    price: "$95",
    studentLimit: "Up to 100 students",
    description: "Perfect for new and growing schools",
    featured: false,
  },
  {
    id: "price_1Rt6WFKQZoTFogHviI6tHh1y",
    name: "Scale",
    price: "$125",
    studentLimit: "Up to 200 students",
    description: "Ideal for expanding schools",
    featured: true,
  },
  {
    id: "price_1Rt6XuKQZoTFogHvVqKkLz6l",
    name: "Expand",
    price: "$165",
    studentLimit: "Up to 300 students",
    description: "For established, growing rosters",
    featured: false,
  },
  {
    id: "price_1Rt6jzKQZoTFogHvyiCnAhOr",
    name: "Grow",
    price: "$195",
    studentLimit: "Up to 500 students",
    description: "For large schools with deep rosters",
    featured: false,
  },
  {
    id: "price_1TFKxLKQZoTFogHv8J7FlQ8V",
    name: "Master",
    price: "$245",
    studentLimit: "501+ students",
    description: "For premier schools with unlimited growth",
    featured: false,
  },
] as const;

const FREE_PLAN_UNLOCKS: readonly string[] = [
  "Store, POS & full inventory management",
  "Automated appointment SMS for leads",
  "Expiring credit card reminders for students and parents",
  "Self-service payment links for students and parents",
  "AI student drop-off analytics",
  "Full dashboard analytics & reporting",
  "Paid event registration checkout",
  "Family discounts",
  "Discount codes",
  "Balance adjustments",
  "Website lead capture forms",
  "SMS & push notifications",
  "Write with AI for email & SMS",
  "Additional email templates",
  "More than 3 staff with clock-in, hours tracking, pay periods & payroll export",
  "Multi-staff sub-accounts",
];

// Security constants
const SUBMISSION_COOLDOWN = 2000; // 2 seconds between submissions

// Security event logging
const logSecurityEvent = (event: string, details: any) => {
  console.warn(`Security Event: ${event}`, {
    timestamp: new Date().toISOString(),
    userAgent: navigator.userAgent,
    details
  });
};

const BillingSettings = () => {
  const { getAuthToken } = useAuth();
  const { isFreePlan, plan, refresh } = useStudioPlan();
  const [upgradeLoadingPlanId, setUpgradeLoadingPlanId] = useState<string | null>(null);
  
  // Security-related state
  const [csrfToken, setCsrfToken] = useState<string>('');
  const [lastSubmissionTime, setLastSubmissionTime] = useState<number>(0);
  
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState({
    show: false,
    message: "",
    title: "",
  });
  const [studentLimit, setStudentLimit] = useState<number | null>(null);
  const [limitLoading, setLimitLoading] = useState(true);
  const [currentStudentCount, setCurrentStudentCount] = useState<number | null>(
    null
  );
  const [countLoading, setCountLoading] = useState(true);
  const [studio, setStudio] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [currentPlan, setCurrentPlan] = useState<any>(null);
  const [planLoading, setPlanLoading] = useState(true);
  const [previousPriceId, setPreviousPriceId] = useState<string | null>(null);
  const [previousPlanName, setPreviousPlanName] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [deletionRequest, setDeletionRequest] = useState<any>(null);
  const [hasDeletionRequest, setHasDeletionRequest] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const forceBillingSetup = searchParams.get("setup") === "1";

  // Fetch CSRF token
  const fetchCsrfToken = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/supabase/csrf-token`);
      if (response.ok) {
        const data = await response.json();
        if (data.token) {
          setCsrfToken(data.token);
        } else {
          logSecurityEvent('CSRF_TOKEN_INVALID_RESPONSE', { data });
        }
      } else {
        logSecurityEvent('CSRF_TOKEN_FETCH_ERROR', { status: response.status, statusText: response.statusText });
      }
    } catch (error) {
      logSecurityEvent('CSRF_TOKEN_FETCH_FAILED', { error: error instanceof Error ? error.message : 'Unknown error' });
      // In development, generate a fallback token
      if (process.env.NODE_ENV !== 'production') {
        const fallbackToken = Math.random().toString(36).substring(2) + Date.now().toString(36);
        setCsrfToken(fallbackToken);
        console.warn('Using fallback CSRF token in development mode');
      }
    }
  };

  // Single useEffect to fetch CSRF token once on component mount
  useEffect(() => {
    fetchCsrfToken();
  }, []); // Empty dependency array - only run once

  useEffect(() => {
    if (!csrfToken) return;

    const pendingUpgrade =
      sessionStorage.getItem(PENDING_PLATFORM_UPGRADE_KEY) === "1";
    const shouldReconcile = isFreePlan || forceBillingSetup || pendingUpgrade;
    if (!shouldReconcile) return;

    async function reconcileUpgrade() {
      try {
        const authToken = getAuthToken();
        if (!authToken) return;

        const sessionId = searchParams.get("session_id");
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/platform-billing/reconcile-free-upgrade`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${authToken}`,
              "X-CSRF-Token": csrfToken,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(
              sessionId ? { sessionId } : {}
            ),
          }
        );

        if (response.ok) {
          const data = await response.json();
          if (data.upgraded) {
            await refresh();
            if (!forceBillingSetup) {
              redirectToBillingSetupWizard();
            }
          }
        }
      } catch (error) {
        logSecurityEvent("RECONCILE_FREE_UPGRADE_FAILED", {
          error: error instanceof Error ? error.message : "Unknown error",
        });
      }
    }

    reconcileUpgrade();
  }, [
    csrfToken,
    isFreePlan,
    forceBillingSetup,
    getAuthToken,
    searchParams,
    refresh,
  ]);

  useEffect(() => {
    async function fetchLimit() {
      setLimitLoading(true);
      try {
        const authToken = getAuthToken();
        if (!authToken) return;

        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/platform-billing/student-limit`,
          {
            headers: {
              Authorization: `Bearer ${authToken}`,
              "X-CSRF-Token": csrfToken,
            },
          }
        );
        if (response.ok) {
          const data = await response.json();
          setStudentLimit(data.studentLimit);
        } else {
          setStudentLimit(null);
        }
      } catch (error) {
        logSecurityEvent('FETCH_STUDENT_LIMIT_FAILED', { 
          error: error instanceof Error ? error.message : 'Unknown error' 
        });
        setStudentLimit(null);
      } finally {
        setLimitLoading(false);
      }
    }
    
    // Only fetch if we have a CSRF token
    if (csrfToken) {
      fetchLimit();
    }
  }, [getAuthToken, csrfToken]);

  useEffect(() => {
    async function fetchStudentCount() {
      setCountLoading(true);
      try {
        const authToken = getAuthToken();
        if (!authToken) return;

        const studioRes = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/supabase/studios/me`,
          { 
            headers: { 
              Authorization: `Bearer ${authToken}`,
              "X-CSRF-Token": csrfToken,
            } 
          }
        );
        const studio = await studioRes.json();
        if (!studio?.id) throw new Error("No studio found");

        // Use the new endpoint to count only students with subscriptions
        const studentsRes = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/supabase/students/${studio.id}/with-subscriptions`,
          { 
            headers: { 
              Authorization: `Bearer ${authToken}`,
              "X-CSRF-Token": csrfToken,
            } 
          }
        );
        const studentsData = await studentsRes.json();
        setCurrentStudentCount(studentsData.count || 0);
      } catch (error) {
        logSecurityEvent('FETCH_STUDENT_COUNT_FAILED', { 
          error: error instanceof Error ? error.message : 'Unknown error' 
        });
        setCurrentStudentCount(null);
      } finally {
        setCountLoading(false);
      }
    }
    
    // Only fetch if we have a CSRF token
    if (csrfToken) {
      fetchStudentCount();
    }
  }, [getAuthToken, csrfToken]);

  useEffect(() => {
    async function fetchCurrentPlan() {
      setPlanLoading(true);
      try {
        const authToken = getAuthToken();
        if (!authToken) return;

        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/platform-billing/current-plan`,
          {
            headers: {
              Authorization: `Bearer ${authToken}`,
              "X-CSRF-Token": csrfToken,
            },
          }
        );
        if (response.ok) {
          const data = await response.json();
          setCurrentPlan(data);
        } else {
          setCurrentPlan(null);
        }
      } catch (error) {
        logSecurityEvent('FETCH_CURRENT_PLAN_FAILED', { 
          error: error instanceof Error ? error.message : 'Unknown error' 
        });
        setCurrentPlan(null);
      } finally {
        setPlanLoading(false);
      }
    }
    
    // Only fetch if we have a CSRF token
    if (csrfToken) {
      fetchCurrentPlan();
    }
  }, [getAuthToken, csrfToken]);

  useEffect(() => {
    async function fetchStudio() {
      try {
        // Fetching initial studio data
        const authToken = getAuthToken();
        if (!authToken) return;

        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/supabase/studios/me`,
          {
            headers: {
              Authorization: `Bearer ${authToken}`,
              "X-CSRF-Token": csrfToken,
            },
          }
        );
        if (!res.ok) throw new Error("Failed to fetch studio");
        const data = await res.json();
        // Initial studio data loaded successfully
        setStudio(data);
      } catch (err) {
        logSecurityEvent('FETCH_STUDIO_FAILED', { 
          error: err instanceof Error ? err.message : 'Unknown error' 
        });
        setError("Failed to load studio data");
      } finally {
        setLoading(false);
      }
    }
    
    // Only fetch if we have a CSRF token
    if (csrfToken) {
      fetchStudio();
    }
  }, [getAuthToken, csrfToken]);

  useEffect(() => {
    async function checkSubscriptionStatus() {
      try {
        // Checking subscription status
        const authToken = getAuthToken();
        if (!authToken) return;

        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/platform-billing/subscription/check`,
          {
            headers: {
              Authorization: `Bearer ${authToken}`,
              "X-CSRF-Token": csrfToken,
            },
          }
        );
        const data = await response.json();
        // Subscription check response received

        // If we have a subscription, refresh the studio data
        if (data.hasSubscription) {
          // Active subscription found, fetching studio data
          const studioRes = await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/supabase/studios/me`,
            {
              headers: {
                Authorization: `Bearer ${authToken}`,
                "X-CSRF-Token": csrfToken,
              },
            }
          );
          if (studioRes.ok) {
            const studioData = await studioRes.json();
            // Studio data received successfully
            setStudio(studioData);
          } else {
            logSecurityEvent('FETCH_STUDIO_AFTER_SUBSCRIPTION_FAILED', { 
              status: studioRes.status 
            });
          }
        } else {
          // No active subscription found
        }
      } catch (error) {
        logSecurityEvent('CHECK_SUBSCRIPTION_STATUS_FAILED', { 
          error: error instanceof Error ? error.message : 'Unknown error' 
        });
      }
    }

    // Only check if we have a CSRF token
    if (csrfToken) {
      checkSubscriptionStatus();
    }
  }, [getAuthToken, csrfToken]);

  useEffect(() => {
    async function fetchPreviousSubscription() {
      const authToken = getAuthToken();
      if (!authToken) return;

      try {
        // Fetch the most recent subscription (including canceled ones)
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/platform-billing/recent-subscription`,
          {
            headers: {
              Authorization: `Bearer ${authToken}`,
              "X-CSRF-Token": csrfToken,
            },
          }
        );

        if (response.ok) {
          const data = await response.json();
          if (data.hasSubscription && data.priceId) {
            setPreviousPriceId(data.priceId);
            setPreviousPlanName(data.planName);
          } else {
            // Default to Launch plan if no previous subscription found
            setPreviousPriceId("price_1Rt6VQKQZoTFogHvqWkAKnGl");
            setPreviousPlanName("Launch");
          }
        }
      } catch (error) {
        logSecurityEvent('FETCH_PREVIOUS_SUBSCRIPTION_FAILED', { 
          error: error instanceof Error ? error.message : 'Unknown error' 
        });
        // Default to Launch plan if we can't determine previous plan
        setPreviousPriceId("price_1Rt6VQKQZoTFogHvqWkAKnGl");
        setPreviousPlanName("Launch");
      }
    }

    // Only fetch if we have a CSRF token
    if (csrfToken) {
      fetchPreviousSubscription();
    }
  }, [getAuthToken, csrfToken]);

  useEffect(() => {
    async function fetchDeletionRequest() {
      const authToken = getAuthToken();
      if (!authToken) return;

      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/supabase/studios/me/delete-request`,
          {
            headers: {
              Authorization: `Bearer ${authToken}`,
              "X-CSRF-Token": csrfToken,
            },
          }
        );

        if (response.ok) {
          const data = await response.json();
          setHasDeletionRequest(data.hasDeletionRequest);
          setDeletionRequest(data.deletionRequest);
        }
      } catch (error) {
        logSecurityEvent('FETCH_DELETION_REQUEST_FAILED', { 
          error: error instanceof Error ? error.message : 'Unknown error' 
        });
      }
    }

    // Only fetch if we have a CSRF token
    if (csrfToken) {
      fetchDeletionRequest();
    }
  }, [getAuthToken, csrfToken]);

  const handleUpgradeFromFree = async (priceId: string) => {
    const now = Date.now();
    if (now - lastSubmissionTime < SUBMISSION_COOLDOWN) {
      setNotification({
        show: true,
        message: "Please wait before submitting again",
        title: "Error",
      });
      return;
    }

    if (!csrfToken && process.env.NODE_ENV === 'production') {
      setNotification({
        show: true,
        message: "Security token missing. Please refresh the page and try again.",
        title: "Error",
      });
      return;
    }

    setUpgradeLoadingPlanId(priceId);
    setLastSubmissionTime(now);
    sessionStorage.setItem(PENDING_PLATFORM_UPGRADE_KEY, "1");

    try {
      const authToken = getAuthToken();
      if (!authToken) throw new Error("Not authenticated");

      const returnUrl = `${window.location.origin}/app/upgrade-complete`;
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/platform-billing/create-checkout-session`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
            "X-CSRF-Token": csrfToken,
          },
          body: JSON.stringify({ priceId, returnUrl }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || "Failed to start upgrade checkout");
      }

      const { url } = await response.json();
      window.location.href = url;
    } catch (error) {
      setNotification({
        show: true,
        message:
          error instanceof Error ? error.message : "Failed to start upgrade",
        title: "Error",
      });
      setUpgradeLoadingPlanId(null);
    }
  };

  const handleRestartSubscription = async () => {
    // Rate limiting check
    const now = Date.now();
    if (now - lastSubmissionTime < SUBMISSION_COOLDOWN) {
      setNotification({
        show: true,
        message: "Please wait before submitting again",
        title: "Error",
      });
      return;
    }

    // CSRF token validation
    if (!csrfToken) {
      logSecurityEvent('CSRF_TOKEN_MISSING', { action: 'restartSubscription' });
      if (process.env.NODE_ENV === 'production') {
        setNotification({
          show: true,
          message: "Security token missing. Please refresh the page and try again.",
          title: "Error",
        });
        return;
      } else {
        console.warn('CSRF token missing in development mode - proceeding with submission');
      }
    }

    setLoading(true);
    setLastSubmissionTime(now);
    
    try {
      const currentUrl = window.location.origin + window.location.pathname;
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/platform-billing/create-checkout-session`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getAuthToken()}`,
            "X-CSRF-Token": csrfToken,
          },
          body: JSON.stringify({
            priceId: previousPriceId || "price_1Rt6VQKQZoTFogHvqWkAKnGl", // Use previous plan or default to Launch
            returnUrl: currentUrl, // Return to this page after checkout
          }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        // Redirect to Stripe Checkout
        window.location.href = data.url;
      } else {
        const errorData = await response.json();
        logSecurityEvent('CREATE_CHECKOUT_SESSION_FAILED', { 
          error: errorData.error || 'Unknown error',
          status: response.status 
        });
        setNotification({
          show: true,
          message: `Failed to create checkout session: ${errorData.error || 'Unknown error'}`,
          title: "Error",
        });
      }
    } catch (error) {
      logSecurityEvent('RESTART_SUBSCRIPTION_FAILED', { 
        error: error instanceof Error ? error.message : 'Unknown error' 
      });
      setNotification({
        show: true,
        message: "Failed to restart subscription. Please try again.",
        title: "Error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleManageBilling = async () => {
    // Rate limiting check
    const now = Date.now();
    if (now - lastSubmissionTime < SUBMISSION_COOLDOWN) {
      setNotification({
        show: true,
        message: "Please wait before submitting again",
        title: "Error",
      });
      return;
    }

    // CSRF token validation
    if (!csrfToken) {
      logSecurityEvent('CSRF_TOKEN_MISSING', { action: 'manageBilling' });
      if (process.env.NODE_ENV === 'production') {
        setNotification({
          show: true,
          message: "Security token missing. Please refresh the page and try again.",
          title: "Error",
        });
        return;
      } else {
        console.warn('CSRF token missing in development mode - proceeding with submission');
      }
    }

    const authToken = getAuthToken();
    if (!authToken) return;
    
    setLoading(true);
    setLastSubmissionTime(now);
    
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/platform-billing/create-customer-portal-session`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
            "X-CSRF-Token": csrfToken,
          },
          body: JSON.stringify({
            returnUrl: window.location.href,
          }),
        }
      );
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to open billing portal");
      }
      const { url } = await response.json();
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      logSecurityEvent('MANAGE_BILLING_FAILED', { 
        error: error instanceof Error ? error.message : 'Unknown error' 
      });
      setNotification({
        show: true,
        message: error instanceof Error ? error.message : "An error occurred",
        title: "Error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    // Rate limiting check
    const now = Date.now();
    if (now - lastSubmissionTime < SUBMISSION_COOLDOWN) {
      setNotification({
        show: true,
        message: "Please wait before submitting again",
        title: "Error",
      });
      return;
    }

    // CSRF token validation
    if (!csrfToken) {
      logSecurityEvent('CSRF_TOKEN_MISSING', { action: 'deleteAccount' });
      if (process.env.NODE_ENV === 'production') {
        setNotification({
          show: true,
          message: "Security token missing. Please refresh the page and try again.",
          title: "Error",
        });
        return;
      } else {
        console.warn('CSRF token missing in development mode - proceeding with submission');
      }
    }

    setDeletingAccount(true);
    setLastSubmissionTime(now);
    
    try {
      const authToken = getAuthToken();
      if (!authToken) return;

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/studios/me/delete-request`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
            "X-CSRF-Token": csrfToken,
          },
        }
      );

      if (response.ok) {
        setNotification({
          show: true,
          message: "Your account deletion request has been submitted. Your data will be permanently deleted in 30 days, unless your undo your deletion request, and reactivate your subscription.",
          title: "Deletion Request Submitted",
        });
        setShowDeleteModal(false);
        // Refresh deletion request status
        const statusResponse = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/supabase/studios/me/delete-request`,
          {
            headers: {
              Authorization: `Bearer ${authToken}`,
              "X-CSRF-Token": csrfToken,
            },
          }
        );
        if (statusResponse.ok) {
          const data = await statusResponse.json();
          setHasDeletionRequest(data.hasDeletionRequest);
          setDeletionRequest(data.deletionRequest);
        }
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to submit deletion request");
      }
    } catch (error) {
      logSecurityEvent('DELETE_ACCOUNT_FAILED', { 
        error: error instanceof Error ? error.message : 'Unknown error' 
      });
      setNotification({
        show: true,
        message: error instanceof Error ? error.message : "An error occurred while submitting deletion request",
        title: "Error",
      });
    } finally {
      setDeletingAccount(false);
    }
  };

  const handleCancelDeletionRequest = async () => {
    // Rate limiting check
    const now = Date.now();
    if (now - lastSubmissionTime < SUBMISSION_COOLDOWN) {
      setNotification({
        show: true,
        message: "Please wait before submitting again",
        title: "Error",
      });
      return;
    }

    // CSRF token validation
    if (!csrfToken) {
      logSecurityEvent('CSRF_TOKEN_MISSING', { action: 'cancelDeletionRequest' });
      if (process.env.NODE_ENV === 'production') {
        setNotification({
          show: true,
          message: "Security token missing. Please refresh the page and try again.",
          title: "Error",
        });
        return;
      } else {
        console.warn('CSRF token missing in development mode - proceeding with submission');
      }
    }

    setDeletingAccount(true);
    setLastSubmissionTime(now);
    
    try {
      const authToken = getAuthToken();
      if (!authToken) return;

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/studios/me/delete-request`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
            "X-CSRF-Token": csrfToken,
          },
        }
      );

      if (response.ok) {
        setNotification({
          show: true,
          message: "Your deletion request has been cancelled successfully.",
          title: "Deletion Request Cancelled",
        });
        setHasDeletionRequest(false);
        setDeletionRequest(null);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to cancel deletion request");
      }
    } catch (error) {
      logSecurityEvent('CANCEL_DELETION_REQUEST_FAILED', { 
        error: error instanceof Error ? error.message : 'Unknown error' 
      });
      setNotification({
        show: true,
        message: error instanceof Error ? error.message : "An error occurred while cancelling deletion request",
        title: "Error",
      });
    } finally {
      setDeletingAccount(false);
    }
  };

  // Get plan name from current plan data
  const getPlanName = () => {
    if (isFreePlan) return "Free";
    if (!currentPlan) return "No plan selected";
    if (currentPlan.planName && currentPlan.planName !== "Unknown") {
      return currentPlan.planName;
    }
    return `Plan (${currentPlan.priceId})`;
  };

  // Get student limit display text
  const getStudentLimitText = () => {
    if (isFreePlan && plan?.capabilities.maxStudents != null) {
      return plan.capabilities.maxStudents.toString();
    }
    if (limitLoading) return "Loading...";
    if (studentLimit === null) return "Unlimited";
    return studentLimit.toString();
  };

  // Get student count display text
  const getStudentCountText = () => {
    if (countLoading) return "Loading...";
    if (currentStudentCount === null) return "Error";
    return currentStudentCount.toString();
  };

  // Check if account deletion should be allowed
  const canDeleteAccount = () => {
    if (isFreePlan) return true;
    // Allow deletion if no active subscription or if subscription is cancelled
    if (!studio?.is_active) return true;
    
    // If there's a current plan, check if it's cancelled or set to cancel at period end
    if (currentPlan?.status === 'canceled' || currentPlan?.cancel_at_period_end === true) {
      return true;
    }
    
    // Check if subscription is in a state that allows deletion
    // Stripe subscriptions can be 'active' but still cancelled (cancel_at_period_end = true)
    if (currentPlan?.status === 'active' && currentPlan?.cancel_at_period_end === true) {
      return true;
    }
    
    // If no current plan data, fall back to studio active status
    return !studio?.is_active;
  };

  return (
    <div className="space-y-6">
        <Notification
          show={notification.show}
          message={notification.message}
          title={notification.title}
          onClose={() => setNotification((prev) => ({ ...prev, show: false }))}
        />
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
            Billing Settings
          </h2>

          {(forceBillingSetup || !isFreePlan) && (
            <BillingSetupWizard
              getAuthToken={getAuthToken}
              csrfToken={csrfToken}
              forceShow={forceBillingSetup}
            />
          )}
          
          {/* Billing Information Banner */}
          {isFreePlan ? (
            <div className="mb-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3 flex-1">
                  <h3 className="text-sm font-medium text-blue-800 dark:text-blue-200">
                    Upgrading from Free
                  </h3>
                  <div className="mt-2 text-sm text-blue-700 dark:text-blue-300">
                    <p>
                      Choose a paid plan below to unlock billing, messaging, kiosk mode, and
                      analytics. You&apos;ll keep your existing students and studio data.
                    </p>
                    <p className="mt-2 text-sm text-blue-700 dark:text-blue-300">
                      Billing starts when you upgrade — no separate trial period on top of the
                      free plan.
                    </p>
                    <p className="mt-3 text-xs text-blue-600 dark:text-blue-400">
                      <strong>Note:</strong> SMS rental and usage charges are managed separately in Settings → Integrations.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
          <div className="mb-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <h3 className="text-sm font-medium text-blue-800 dark:text-blue-200">
                  How Billing Works
                </h3>
                <div className="mt-2 text-sm text-blue-700 dark:text-blue-300">
                  <ul className="list-disc pl-5 space-y-1">
                    <li>
                      <strong>Monthly Membership:</strong> Your plan subscription is billed monthly on the same day you subscribed.
                    </li>
                  </ul>
                  <p className="mt-3 text-xs text-blue-600 dark:text-blue-400">
                    <strong>Note:</strong> SMS rental and usage charges are managed separately in Settings → Integrations.
                  </p>
                </div>
              </div>
            </div>
          </div>
          )}

          {loading ? (
            <div className="text-gray-600 dark:text-gray-400">Loading...</div>
          ) : error ? (
            <div className="text-red-600 dark:text-red-400">{error}</div>
          ) : (
            <>
              {isFreePlan ? (
                <div className="space-y-6">
                  <div className="rounded-xl border border-indigo-200 dark:border-indigo-800 bg-indigo-50 dark:bg-indigo-950/40 p-5">
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
                      Unlock with a paid plan
                    </h4>
                    <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {FREE_PLAN_UNLOCKS.map((featureLabel) => (
                        <li
                          key={featureLabel}
                          className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300"
                        >
                          <CheckIcon className="h-4 w-4 shrink-0 text-indigo-600 dark:text-indigo-400" />
                          {featureLabel}
                        </li>
                      ))}
                    </ul>
                    <p className="mt-4 inline-flex items-center rounded-full bg-emerald-100 px-3 py-1.5 text-sm font-semibold text-emerald-800 ring-1 ring-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-200 dark:ring-emerald-700">
                      Together, these features help studios reclaim up to 10 hours of admin time every week
                    </p>
                  </div>

                  <div>
                    <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                      Choose a plan
                    </h3>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                      All paid plans include the full feature set. Billed monthly — cancel anytime.
                    </p>
                    <div className="mt-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                      {PAID_UPGRADE_PLANS.map((paidPlan) => (
                        <div
                          key={paidPlan.id}
                          className={`relative flex flex-col rounded-xl border p-5 ${
                            paidPlan.featured
                              ? "border-indigo-600 dark:border-indigo-500 ring-2 ring-indigo-600/20 dark:ring-indigo-500/30 bg-white dark:bg-gray-800"
                              : "border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
                          }`}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <h4
                              className={`text-lg font-semibold ${
                                paidPlan.featured
                                  ? "text-indigo-600 dark:text-indigo-400"
                                  : "text-gray-900 dark:text-white"
                              }`}
                            >
                              {paidPlan.name}
                            </h4>
                            {paidPlan.featured && (
                              <span className="shrink-0 rounded-full bg-indigo-600/10 dark:bg-indigo-400/10 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-indigo-600 dark:text-indigo-400">
                                Popular
                              </span>
                            )}
                          </div>
                          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                            {paidPlan.description}
                          </p>
                          <p className="mt-4 flex items-baseline gap-x-1">
                            <span className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
                              {paidPlan.price}
                            </span>
                            <span className="text-sm text-gray-500 dark:text-gray-400">/month</span>
                          </p>
                          <p className="mt-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                            {paidPlan.studentLimit}
                          </p>
                          <button
                            type="button"
                            onClick={() => handleUpgradeFromFree(paidPlan.id)}
                            disabled={!!upgradeLoadingPlanId}
                            className={`mt-5 w-full rounded-lg px-4 py-2.5 text-sm font-semibold transition-colors disabled:opacity-50 ${
                              paidPlan.featured
                                ? "bg-indigo-600 text-white hover:bg-indigo-500"
                                : "border border-indigo-300 dark:border-indigo-600 bg-white dark:bg-gray-800 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/20"
                            }`}
                          >
                            {upgradeLoadingPlanId === paidPlan.id
                              ? "Redirecting to checkout..."
                              : `Upgrade to ${paidPlan.name}`}
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
              <div className="space-y-4">
                <div className="space-y-2">
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Current Plan: {planLoading ? "Loading..." : getPlanName()}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Students: {getStudentCountText()} / {getStudentLimitText()}
                  </p>
                  {currentStudentCount !== null &&
                    studentLimit !== null &&
                    currentStudentCount >= studentLimit && (
                      <p className="text-sm text-red-600 dark:text-red-400 font-medium">
                        ⚠️ You have reached your student limit. Please upgrade
                        your plan to add more students.
                      </p>
                    )}
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={handleManageBilling}
                    disabled={loading}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="pointer-events-none">{loading ? "Loading..." : studio?.is_active ? "Manage Billing" : "Access Billing Portal"}</span>
                  </button>
                  {!studio?.is_active && (
                    <button
                      onClick={handleRestartSubscription}
                      disabled={loading}
                      className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md shadow-sm text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="pointer-events-none">{loading ? "Loading..." : `Restart ${previousPlanName || "Launch"} Plan`}</span>
                    </button>
                  )}
                </div>
                {!studio?.is_active && (
                  <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md p-4">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                          Subscription Inactive
                        </h3>
                        <div className="mt-2 text-sm text-yellow-700 dark:text-yellow-300">
                          <p>
                            Your subscription is currently inactive. You have two options:
                          </p>
                          <ul className="mt-2 list-disc list-inside space-y-1">
                            <li><strong>Restart {previousPlanName || "Launch"} Plan:</strong> Immediately reactivate with your previous plan</li>
                            <li><strong>Access Billing Portal:</strong> Manage payment methods and billing history</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              )}

              {/* Delete Account Section */}
              <div className="border-t border-gray-200 dark:border-gray-700 pt-6 mt-6">
                  {hasDeletionRequest && deletionRequest?.status === 'pending' ? (
                    <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md p-4">
                      <div className="flex">
                        <div className="flex-shrink-0">
                          <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div className="ml-3">
                          <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                            Account Deletion Scheduled
                          </h3>
                          <div className="mt-2 text-sm text-yellow-700 dark:text-yellow-300">
                            <p>
                              Your account deletion request was submitted on{' '}
                              {new Date(deletionRequest.requested_at).toLocaleDateString()}.
                            </p>
                            <p className="mt-1">
                              Your data will be permanently deleted on{' '}
                              {new Date(new Date(deletionRequest.requested_at).getTime() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString()}.
                            </p>
                            <div className="mt-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md p-3">
                              <p className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-2">
                                After undoing deletion, remember to:
                              </p>
                              <ul className="text-sm text-blue-700 dark:text-blue-300 space-y-1">
                                <li>• Reactivate your subscription if it was cancelled</li>
                                <li>• Update your payment method if needed</li>
                                <li>• Ensure your billing is up to date</li>
                              </ul>
                            </div>
                          </div>
                          <div className="mt-4">
                            <button
                              onClick={handleCancelDeletionRequest}
                              disabled={deletingAccount}
                              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                            >
                              <span className="pointer-events-none">{deletingAccount ? "Undoing..." : "Undo Delete Account"}</span>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-4">
                      <div className="flex">
                        <div className="flex-shrink-0">
                          <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div className="ml-3">
                          <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
                            Delete Account
                          </h3>
                          <div className="mt-2 text-sm text-red-700 dark:text-red-300">
                            <p>
                              This will permanently delete your account and all associated data.
                            </p>
                            {!canDeleteAccount() && (
                              <p className="mt-2 font-medium">
                                ⚠️ You must cancel your active subscription before deleting your account. Press "Manage Billing".
                              </p>
                            )}
                          </div>
                          <div className="mt-4">
                            <button
                              onClick={() => setShowDeleteModal(true)}
                              disabled={!canDeleteAccount()}
                              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                            >
                              <span className="pointer-events-none">Delete Account</span>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
              </div>
            </>
          )}
        </div>

      {/* Delete Account Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4 sm:p-6 w-full max-w-md">
              <div className="absolute right-0 top-0 pr-4 pt-4">
                <button
                  type="button"
                  className="rounded-md bg-white dark:bg-gray-800 text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                  onClick={() => setShowDeleteModal(false)}
                >
                  <span className="sr-only pointer-events-none">Close</span>
                  <svg className="h-6 w-6 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="mt-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                  Delete Account
                </h3>
                                  <div className="text-sm text-gray-600 dark:text-gray-300 space-y-4 text-left">
                    <p>
                      <strong>Important:</strong> Your data will be saved for 30 days. After 30 days, you cannot recover your data, nor log back into your account and reactivate your account.
                    </p>
                    <p>
                      If you wish to reactivate your account within 30 days, please log back in and reactivate your subscription if it's past the cancellation date.
                    </p>
                    <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md p-3">
                      <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200 mb-2">
                        Before deleting, please export your data:
                      </p>
                      <ul className="text-sm text-yellow-700 dark:text-yellow-300 space-y-1">
                        <li>• Student data</li>
                        <li>• Lead data</li>
                        <li>• Waiver data</li>
                        <li>• Events data</li>
                      </ul>
                    </div>
                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md p-3">
                      <p className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-2">
                        After undoing deletion, remember to:
                      </p>
                      <ul className="text-sm text-blue-700 dark:text-blue-300 space-y-1">
                        <li>• Reactivate your subscription if it was cancelled</li>
                        <li>• Update your payment method if needed</li>
                        <li>• Ensure your billing is up to date</li>
                      </ul>
                    </div>
                  </div>
              </div>
              <div className="flex flex-col xl:flex-row xl:justify-end space-y-3 xl:space-y-0 xl:space-x-3 mt-6">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  disabled={deletingAccount}
                  className="w-full xl:w-auto px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="pointer-events-none">Cancel</span>
                </button>
                <button
                  onClick={handleDeleteAccount}
                  disabled={deletingAccount}
                  className="w-full xl:w-auto px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="pointer-events-none">{deletingAccount ? "Deleting..." : "Confirm Delete"}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BillingSettings;
